import { toBeHex } from 'ethers'

import { WARNINGS } from '../../consts/signAccountOp/errorHandling'
import { Price } from '../../interfaces/assets'
import { Hex } from '../../interfaces/hex'
import { TraceCallDiscoveryStatus, Warning } from '../../interfaces/signAccountOp'
import { AccountOp } from '../../libs/accountOp/accountOp'
import { FeePaymentOption } from '../../libs/estimate/interfaces'
import { shouldDisplaySafeDelegateCallWarning } from '../../libs/humanizer/modules/Safe'
import { TokenResult } from '../../libs/portfolio'
import { getAccountPortfolioTotal, getTotal } from '../../libs/portfolio/helpers'
import { AccountState } from '../../libs/portfolio/interfaces'
import { GasSpeeds } from '../../services/bundlers/types'
import { safeTokenAmountAndNumberMultiplication } from '../../utils/numbers/formatters'

export type SignAccountOpType = 'default' | 'one-click-swap-and-bridge' | 'one-click-transfer'

function getFeeSpeedIdentifier(option: FeePaymentOption, accountAddr: string) {
  return `${option.paidBy}:${option.token.address}:${option.token.symbol.toLowerCase()}:${
    option.token.flags.onGasTank ? 'gasTank' : 'feeToken'
  }`
}

function getTokenUsdAmount(token: TokenResult, gasAmount: bigint): string {
  const isUsd = (price: Price) => price.baseCurrency === 'usd'
  const usdPrice = token.priceIn.find(isUsd)?.price

  if (!usdPrice) return ''

  return safeTokenAmountAndNumberMultiplication(gasAmount, token.decimals, usdPrice)
}

function getSignificantBalanceDecreaseWarning(
  portfolioState: AccountState,
  chainId: bigint,
  traceCallDiscoveryStatus: TraceCallDiscoveryStatus
): Warning | null {
  const portfolioNetworkState = portfolioState?.[chainId.toString()]

  if (portfolioNetworkState && portfolioNetworkState.result && !portfolioNetworkState.isLoading) {
    const totalInUSD = getAccountPortfolioTotal(
      portfolioState,
      ['rewards', 'gasTank', 'projectedRewards'],
      false
    )
    const simulatedTokens = portfolioNetworkState.result.tokens.filter(
      (t) => typeof t.amountPostSimulation === 'bigint'
    )

    if (!simulatedTokens.length) return null

    // Calculates the amount on the pending block * the price of the token
    const simulatedTokensValueBeforeSimulationInUSD = getTotal(simulatedTokens, null, {
      includeHiddenTokens: true,
      beforeSimulation: true
    })?.usd
    // Calculates the amount after the simulation * the price of the token
    const simulatedTokensValueAfterSimulationInUSD = getTotal(simulatedTokens, null, {
      includeHiddenTokens: true,
      beforeSimulation: false
    })?.usd

    if (
      typeof simulatedTokensValueBeforeSimulationInUSD !== 'number' ||
      typeof simulatedTokensValueAfterSimulationInUSD !== 'number'
    )
      return null

    const absoluteDecreaseInUSD =
      simulatedTokensValueBeforeSimulationInUSD - simulatedTokensValueAfterSimulationInUSD

    // In case the balance increased or stayed the same
    if (absoluteDecreaseInUSD <= 0) return null

    const hasSignificantBalanceDecrease =
      absoluteDecreaseInUSD >= totalInUSD * 0.2 && absoluteDecreaseInUSD >= 1000

    if (!hasSignificantBalanceDecrease) return null

    // We wait for the discovery process (main.traceCall) to complete before showing WARNINGS.significantBalanceDecrease.
    // This is important because, in the case of a SWAP to a new token, the new token is not yet part of the portfolio,
    // which could incorrectly trigger a significant balance drop warning.
    // To prevent this, we ensure the discovery process is completed first.
    if (traceCallDiscoveryStatus === TraceCallDiscoveryStatus.Done) {
      return WARNINGS.significantBalanceDecrease
    }

    // If the discovery process takes too long (more than 2 seconds) or fails,
    // we still show a warning, but we indicate that our balance decrease assumption may be incorrect.
    if (
      traceCallDiscoveryStatus === TraceCallDiscoveryStatus.Failed ||
      traceCallDiscoveryStatus === TraceCallDiscoveryStatus.SlowPendingResponse
    ) {
      return WARNINGS.possibleBalanceDecrease
    }
  }

  return null
}

const getUnknownTokenWarning = (pending: AccountState, chainId: bigint): Warning | null => {
  const networkData = pending?.[chainId.toString()]

  if (networkData?.isLoading) return null

  const tokens = networkData?.result?.tokens || []
  const hasUnknownTokens = tokens.some((t) => t.flags.suspectedType)

  return hasUnknownTokens ? WARNINGS.unknownToken : null
}

// A Safe transaction fetched from the Safe{WALLET} queue (accountOp.safeTx) can be a
// delegatecall; the humanizer only sees the calls it decodes from it, so the warning that the
// operation itself is a delegatecall to a non-whitelisted contract lives here (as upstream does).
const getSafeDelegateCallWarning = (accountOp: AccountOp): Warning | null => {
  if (!accountOp.safeTx) return null

  const shouldWarn = shouldDisplaySafeDelegateCallWarning(
    BigInt(accountOp.safeTx.operation),
    accountOp.safeTx.to
  )

  return shouldWarn ? WARNINGS.safeDelegateCall! : null
}

const getFeeTokenPriceUnavailableWarning = (
  hasSpeed: boolean,
  feeTokenHasPrice: boolean
): Warning | null => {
  if (!hasSpeed || feeTokenHasPrice) return null

  return WARNINGS.feeTokenPriceUnavailable
}

/**
 * A transient underpricing: the network's gas price moved between our estimate
 * and the broadcast attempt. Refreshing the gas price and re-estimating fixes
 * it, so the UI should ask the user to retry rather than hard-fail.
 * Mirrors upstream `isUnderpriced`.
 */
const isUnderpriced = (msg: string): boolean => {
  // ethers wraps RPC failures as `could not coalesce error (error={...},
  // payload={...})` and the payload embeds the whole userOperation — including
  // the literal strings "maxFeePerGas"/"maxPriorityFeePerGas". Matching against
  // it labelled unrelated failures (e.g. an AA24 signature error) as a fee
  // change, so only look at the part that carries the reason.
  const reason = msg.split('payload=')[0] || msg

  return (
    reason.includes('underpriced') ||
    reason.includes('Fee confirmation failed') ||
    // PulseChain nodes phrase an under-minimum fee as "transaction gas price
    // below minimum: gas tip cap 138863, minimum needed 10000000000". Without
    // this the user only got the generic "unknown error (Origin: RPC call)"
    // toast and the wallet never refreshed the gas price.
    reason.includes('gas price below minimum') ||
    reason.includes('gas tip cap') ||
    reason.includes('maxFeePerGas') ||
    reason.includes('maxPriorityFeePerGas')
  )
}

/**
 * ZKX-only. The ZKX paymaster rejects a userOp whose offered fee does not cover
 * the full broadcast cost — unlike Ambire's relayer, which debits the observed
 * on-chain cost and therefore tolerates the client's estimate. The client's fee
 * quote omits `paymasterVerificationGasLimit` and (for a 7702 upgrade) the
 * authorization's extra `preVerificationGas`, so the shortfall is STRUCTURAL:
 * refreshing the gas price will not close it and retrying fails identically.
 * Keep this distinct from `isUnderpriced` so we never present it as transient.
 */
const isFeeBelowBroadcastCost = (msg: string): boolean =>
  msg.includes('offered fee is below the cost of broadcasting')

/**
 * ZKX: raise `gasPrices` so every speed clears the replacement-fee floor of a
 * speed-up, while keeping the four speeds distinct.
 *
 * A speed-up used to pin all four speeds to the pending txn's own price, which
 * left the fee-speed dropdown inert and capped the replacement at whatever the
 * original offered - useless when the txn is stuck precisely because that price
 * was too low for the chain. The floor keeps the replacement valid (a lower one
 * is rejected outright); the live network prices and the +10% ladder give the
 * user a real way to outbid the stuck txn.
 */
const applyGasPriceFloor = (
  gasPrices: GasSpeeds,
  floor: { maxFeePerGas: bigint; maxPriorityFeePerGas: bigint }
): GasSpeeds => {
  const speeds: (keyof GasSpeeds)[] = ['slow', 'medium', 'fast', 'ape']
  const raised = { ...gasPrices }
  let prevMaxFeePerGas = 0n
  let prevMaxPriorityFeePerGas = 0n

  speeds.forEach((speed) => {
    let maxFeePerGas = BigInt(gasPrices[speed].maxFeePerGas)
    let maxPriorityFeePerGas = BigInt(gasPrices[speed].maxPriorityFeePerGas || 0)

    if (maxFeePerGas < floor.maxFeePerGas) maxFeePerGas = floor.maxFeePerGas
    if (maxPriorityFeePerGas < floor.maxPriorityFeePerGas)
      maxPriorityFeePerGas = floor.maxPriorityFeePerGas

    // every speed must cost more than the one below it, otherwise the whole
    // ladder collapses onto the floor and the dropdown looks broken again
    if (prevMaxFeePerGas && maxFeePerGas <= prevMaxFeePerGas)
      maxFeePerGas = prevMaxFeePerGas + prevMaxFeePerGas / 10n
    // a legacy (non-1559) chain has no tip at all - leave it at zero
    if (prevMaxPriorityFeePerGas && maxPriorityFeePerGas <= prevMaxPriorityFeePerGas)
      maxPriorityFeePerGas = prevMaxPriorityFeePerGas + prevMaxPriorityFeePerGas / 10n

    prevMaxFeePerGas = maxFeePerGas
    prevMaxPriorityFeePerGas = maxPriorityFeePerGas

    raised[speed] = {
      maxFeePerGas: toBeHex(maxFeePerGas) as Hex,
      maxPriorityFeePerGas: toBeHex(maxPriorityFeePerGas) as Hex
    }
  })

  return raised
}

export {
  getSafeDelegateCallWarning,
  applyGasPriceFloor,
  getFeeSpeedIdentifier,
  getFeeTokenPriceUnavailableWarning,
  getSignificantBalanceDecreaseWarning,
  getTokenUsdAmount,
  getUnknownTokenWarning,
  isFeeBelowBroadcastCost,
  isUnderpriced
}

